import {useCallback, useEffect, useMemo, useReducer, useRef} from 'react';
import isEqual from 'lodash/isEqual';

import {
  FormConfig,
  FormContextType,
  FormErrors,
  FormNavigation,
  FormRuntimeConfig,
  FormState,
  FormStructure,
  FormValidators,
  FormValidatorsContext,
  FormValuesConstraint,
  GetFieldValidation,
  GetGroupValidation,
  GetStepValidation,
  SetValues,
  ValidatorReturn,
} from '../types';
import {getNextHistoryEntry} from '../utils/navigation';
import {
  asGroupNodes,
  asStepNodes,
  getStructureMapping,
  getStructurePaths,
} from '../utils/structure';
import {
  formValidation,
  groupValidation,
  stepValidation,
  validatorIsPromise,
} from '../utils/validation';

import formReducer from './reducer';

/*
 * Shared empty defaults. An absent `initialErrors` or `validators` prop used to
 * mint a fresh `{}` on every render, which is what made the hooks below see a
 * changing dependency. One frozen object per default fixes that without a hook.
 */
const NO_ERRORS = Object.freeze({});
const NO_VALIDATORS = Object.freeze({});

export default function useForm<
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
  K = undefined,
>(config: FormConfig<T, S, K>): FormContextType<T, S, K> {
  const {structure, handleNext, onBeforeNext, onChange, onSubmit} = config;
  /*
   * `additionalProps` and `navigationControllers` only exist on `FormConfig`
   * when `K` / `GetStepsWithNext` resolve to something, so they are not
   * statically readable off it. Widening once here keeps the rest of the hook
   * typed.
   */
  const {
    additionalProps: additionalPropsValue,
    navigationControllers: navigationControllersValue,
  } = config as FormRuntimeConfig<T, S, K>;

  /*
   * This memo cannot go, even though React Compiler would cache it. Two hooks
   * below take `initialValues` as a dependency - the validators context and the
   * seeding effect - and `react/exhaustive-deps` is an error in this repo. Drop
   * the memo and both fail the lint gate with "changes every render", because
   * the linter reasons about the source, not the compiled output.
   *
   * Behaviour does not depend on it: the seeding effect deep-compares against a
   * ref before acting, so a fresh object would cost one bailed effect run, not a
   * loop. This is purely what keeps the two rules from contradicting each other.
   */
  const initialValues = useMemo(
    () => (config.initialValues as T) || ({} as T),
    [config.initialValues],
  );

  /*
   * None of these is state. Three are props with a default and two are pure
   * derivations of the `structure` prop, so none of them is held in state - that
   * also froze them at mount, which meant a caller who changed `validators` or
   * `structure` was silently ignored.
   *
   * The frozen empties matter: `|| {}` would mint a new object whenever the prop
   * is absent, and both of these end up in dependency arrays.
   */
  const initialErrors = config.initialErrors || (NO_ERRORS as FormErrors<T>);
  const validators =
    config.validators || (NO_VALIDATORS as FormValidators<T, S, K>);
  const navigationControllers = navigationControllersValue;

  /*
   * Memoized because `setValues` lists it as a dependency. Recomputing it per
   * render handed `setValues` a new identity every time, and any caller effect
   * depending on `setValues` then re-fired on every render - AssignFee's
   * fee-structure effect turned that into an infinite update loop.
   */
  const structureMapping = useMemo(
    () => getStructureMapping<T, S>(structure),
    [structure],
  );

  // Not memoized: only read by getInitialNavigation, which runs once in the
  // reducer initialiser, so its identity is never a dependency.
  const structurePaths = getStructurePaths<T, S>(structure);

  const getInitialNavigation = (): FormNavigation<T, S> => {
    // Multi-step form
    const steps = asStepNodes<T>(structure);

    if (steps) {
      const firstStep = Object.keys(steps)[0] as FormNavigation<
        T,
        S
      >['currentStep'];

      return {
        currentGroup: undefined as FormNavigation<T, S>['currentGroup'],
        currentStep: firstStep,
        history: [
          {
            group: undefined,
            step: firstStep,
            path: structurePaths[0],
            skipped: false,
          },
        ],
      };
    }

    // Grouped multi-step form
    const groups = asGroupNodes<T>(structure);

    if (groups) {
      const groupNames = Object.keys(groups);
      const stepNames = Object.keys(groups[groupNames[0]]);
      const group = groupNames[0] as FormNavigation<T, S>['currentGroup'];
      const step = stepNames[0] as FormNavigation<T, S>['currentStep'];

      return {
        currentGroup: group,
        currentStep: step,
        history: [
          {
            group,
            step,
            path: structurePaths[1],
            skipped: false,
          },
        ],
      };
    }

    return {
      currentGroup: undefined as FormNavigation<T, S>['currentGroup'],
      currentStep: undefined as FormNavigation<T, S>['currentStep'],
      history: [],
    };
  };

  // useReducer will infer state and action types from formReducer
  const [state, dispatch] = useReducer(formReducer, {
    values: initialValues,
    errors: initialErrors,
    navigation: getInitialNavigation(),
    validating: {},
    isSubmitting: false,
    validation: {
      byField: {},
      byStep: {} as FormState<T, S>['validation']['byStep'],
      byGroup: {},
    },
  });

  const getFieldValidation: GetFieldValidation<T> = useCallback(
    (field) => {
      const {validation} = state;
      const hasError =
        typeof state.errors[field] === 'string' &&
        !!state.errors[field]?.length;

      return !!validation.byField[field] && !hasError;
    },
    [state],
  );

  const getStepValidation: GetStepValidation<T, S> = useCallback(
    (step) => {
      return stepValidation(state, structure, String(step));
    },
    [state, structure],
  );

  const getGroupValidation: GetGroupValidation<T, S> = useCallback(
    (group) => {
      return groupValidation(state, structure, String(group));
    },
    [state, structure],
  );

  const getFormValidation = useCallback(() => {
    return formValidation(state, structure);
  }, [state, structure]);

  const validatorsCtx: FormValidatorsContext<T, S, K> = useMemo(
    () => ({
      ...state,
      additionalProps: additionalPropsValue,
      initialValues,
      initialErrors,
    }),
    [state, additionalPropsValue, initialValues, initialErrors],
  );

  const setValues: SetValues<T> = useCallback(
    (fields) => {
      const fieldNames = Object.keys(fields) as Array<keyof T>;

      /*
       * `onChange` fires here rather than from an effect watching `state.values`.
       * This is the only path that changes values, so notifying from it skips the
       * extra render an effect-then-callback round trip costs the consumer.
       * The deep-equality guard keeps a no-op `setValues` from notifying, which
       * is what the old effect's comparison against the previous values did.
       */
      const nextValues = {...state.values, ...fields};

      if (!isEqual(nextValues, state.values)) {
        onChange?.(nextValues as T);
      }

      fieldNames.forEach((field) => {
        const validator = validators[field] || (() => true);

        const validatorRes = validator(
          fields[field] as T[keyof T],
          validatorsCtx,
        );

        // If async validator (promise)
        if (validatorIsPromise(validatorRes)) {
          // Set value immediately
          dispatch({
            type: 'SET_VALUES_&_VALIDATION',
            payload: {
              values: {[field]: fields[field]} as Partial<T>,
              // Reset errors to undefined
              errors: {[field]: undefined} as FormErrors<T>,
            },
          });

          // Start validating
          dispatch({
            type: 'SET_VALIDATING',
            payload: {field, value: true},
          });

          const resolvePromise = (res: ValidatorReturn) => {
            const isValid = typeof res === 'boolean' ? res : res?.isValid;
            const error = typeof res === 'boolean' ? undefined : res?.error;

            // Set validation result
            dispatch({
              type: 'SET_VALUES_&_VALIDATION',
              payload: {
                validation: {
                  field,
                  value: isValid,
                  structureMapping,
                  structure,
                },
                errors: {[field]: error} as FormErrors<T>,
              },
            });
          };

          Promise.resolve(validatorRes)
            .then(resolvePromise)
            .catch(resolvePromise)
            .finally(() => {
              // Stop validating
              dispatch({
                type: 'SET_VALIDATING',
                payload: {field, value: false},
              });
            });

          return;
        }

        // If sync validator (callback function)
        // const res = validator(fields[field] as T[keyof T], validatorsCtx);
        const isValid =
          typeof validatorRes === 'boolean'
            ? validatorRes
            : validatorRes.isValid;
        const error =
          typeof validatorRes === 'boolean' ? undefined : validatorRes.error;

        // Set value and validation
        dispatch({
          type: 'SET_VALUES_&_VALIDATION',
          payload: {
            values: {[field]: fields[field]} as Partial<T>,
            validation: {
              field,
              value: isValid,
              structureMapping,
              structure,
            },
            // Set/reset errors
            errors: {[field]: error} as FormErrors<T>,
          },
        });
      });
    },
    [
      structure,
      validatorsCtx,
      validators,
      structureMapping,
      state.values,
      onChange,
    ],
  );

  const setErrors = (errors: FormErrors<T>): void =>
    dispatch({type: 'SET_ERRORS', payload: errors});

  const next = useCallback(async () => {
    const newNavigation = structuredClone(state.navigation);
    let continueLoop = true;
    let hasNew = false;
    let onBeforeNextSuccess = true;

    // If current step has `onBeforeNext` function
    if (typeof onBeforeNext?.[newNavigation.currentStep] === 'function') {
      onBeforeNextSuccess = await onBeforeNext[newNavigation.currentStep]({
        ...validatorsCtx,
        setValues,
      });
    }

    // If `onBeforeNext` returns false, stop here
    if (!onBeforeNextSuccess) return;

    while (continueLoop) {
      const nextEntry = getNextHistoryEntry<T, S, K>(
        {
          ...state,
          // Warning! this must be a clone of `state.navigation` to be mutated
          navigation: newNavigation,
        },
        structure,
        structurePaths,
        navigationControllers,
        validatorsCtx,
      );

      if (nextEntry) {
        hasNew = true;

        const canContinue =
          typeof handleNext === 'function'
            ? handleNext?.(validatorsCtx, nextEntry)
            : true;

        nextEntry.skipped = !canContinue;
        newNavigation.history.push(nextEntry);
        newNavigation.currentStep = nextEntry.step;
        newNavigation.currentGroup = nextEntry.group as FormNavigation<
          T,
          S
        >['currentGroup'];

        continueLoop = !canContinue;
      } else {
        continueLoop = false;
      }
    }

    // If no next step
    if (!hasNew) return;

    dispatch({type: 'SET_NAVIGATION', payload: newNavigation});
  }, [
    state,
    structure,
    validatorsCtx,
    onBeforeNext,
    handleNext,
    setValues,
    structurePaths,
    navigationControllers,
  ]);

  const prev = useCallback(
    (count = 1) => {
      const {navigation} = state;
      const finalCount = typeof count === 'number' ? count : 1;

      // Nothing to return to
      if (finalCount > 0 && navigation.history.length <= finalCount) return;

      const newNavigation = structuredClone(navigation);
      let canShowPrev = false;
      let nonSkippedCount = 0;

      while (!canShowPrev) {
        newNavigation.history.pop();

        const last = newNavigation.history[newNavigation.history.length - 1];

        nonSkippedCount += last.skipped ? 0 : 1;

        if (!last.skipped && nonSkippedCount === finalCount) {
          canShowPrev = true;
          newNavigation.currentStep = last.step;
          newNavigation.currentGroup = last.group as FormNavigation<
            T,
            S
          >['currentGroup'];
        }
      }

      dispatch({type: 'SET_NAVIGATION', payload: newNavigation});
    },
    [state],
  );

  const submit = async () => {
    dispatch({type: 'SET_SUBMITTING', payload: true});

    return onSubmit(state.values as T, validatorsCtx).finally(() => {
      dispatch({type: 'SET_SUBMITTING', payload: false});
    });
  };

  /*
   * The previous `initialValues` are held in a ref and compared *inside* the
   * effect. `initialValues` is rebuilt whenever the caller passes an inline
   * object, so the deep-equality guard is what stops this effect from looping —
   * and reading a ref during render is not allowed.
   */
  const prevInitialValuesRef = useRef<T | undefined>(undefined);

  // Seed the form from `initialValues`, and re-seed if the caller changes them
  useEffect(() => {
    if (isEqual(initialValues, prevInitialValuesRef.current)) return;

    prevInitialValuesRef.current = initialValues;

    setValues(initialValues);
  }, [initialValues, setValues]);

  const ctx: FormContextType<T, S, K> = {
    ...state,
    initialValues,
    initialErrors,
    additionalProps: additionalPropsValue,
    submit,
    setValues,
    setErrors,
    getFieldValidation,
    getStepValidation,
    getGroupValidation,
    getFormValidation,
    next,
    prev,
  };

  return ctx;
}
